import { Injectable } from '@angular/core';
import { ProjectHeadVO } from '@project/model/vo/project.vo';
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { UserUtils } from '@user/service/user-utils';
import { UserModel } from '@user/model/user.model';
import { ProjectNavigationModel, ProjectNavigationTab } from '../project-navigation.model';
import { ProjectGroupUtils } from '@project/components/project/util/project-group-utils';
import { ProjectNavigationService } from './project-navigation.service';
import { Log } from '@shared/debug/log';
import { firstValueFrom, Subject } from 'rxjs';
import { ProjectComponentPresenter } from '@project/components/project-component.presenter';
import { ProjectModel } from '@project/model/project.model';

/**Service to manage the availability and execution of project-navigation-item actions*/
@Injectable({providedIn: 'root'})
export class ProjectNavigationItemActionsService {

  constructor(
    private userModel: UserModel,
    private projectModel: ProjectModel,
    private projectNavigationService: ProjectNavigationService,
    private projectComponentPresenter: ProjectComponentPresenter,
  ) {
  }

  private _redrawList$ = new Subject<void>();

  /**Fires when a project is unpinned in the favourites view so ProjectNavigationComponents knows to redraw the list*/
  public get redrawList$() {
    return this._redrawList$;
  }

  public async delete(model: ProjectNavigationModel): Promise<void> {
    let success = false;
    if (model.currentSelectedItem instanceof ProjectGroupVO) {
      success = await this.projectNavigationService.deleteGroup(model, model.currentSelectedItem);
    } else if (model.currentSelectedItem instanceof ProjectHeadVO) {
      success = await this.projectNavigationService.deleteConfiguration(model, model.currentSelectedItem);
    } else {
      Log.error("Tried to delete an unknown item type:", typeof(model.currentSelectedItem));
    }
    if (success) {
      model.currentSelectedItem = undefined;
    }
  }

  public async move(model: ProjectNavigationModel): Promise<void> {
    if (model.currentSelectedItem instanceof ProjectGroupVO || model.currentSelectedItem instanceof ProjectHeadVO) {
      await this.projectNavigationService.initMoveProjectOrConfigurationDialog(model, model.currentSelectedItem);
    } else {
      Log.error("Tried to move an unknown item type:", typeof(model.currentSelectedItem));
    }
  }

  public async copy(model: ProjectNavigationModel): Promise<void> {
    if (model.currentSelectedItem instanceof ProjectGroupVO || model.currentSelectedItem instanceof ProjectHeadVO) {
      await this.projectNavigationService.initCopyProjectOrConfigurationDialog(model, model.currentSelectedItem);
    } else {
      Log.error("Tried to copy on unknown item type:", typeof(model.currentSelectedItem));
    }
  }

  public async changeOwner(model: ProjectNavigationModel): Promise<void> {
    if (model.currentSelectedItem instanceof ProjectGroupVO) {
      this.projectNavigationService.initChangeOwnerDialog(model, model.currentSelectedItem);
    } else if (model.currentSelectedItem instanceof ProjectHeadVO) {
      this.projectComponentPresenter.openReassignComponent(model.currentSelectedItem).then((result) => {
        this.projectNavigationService.changeProjectOwnerSuccess(model, model.currentSelectedItem as ProjectHeadVO, result as ProjectHeadVO);
      });
    } else {
      Log.error("Tried to change owner on an unknown item type:", typeof(model.currentSelectedItem));
    }
  }

  public async edit(model: ProjectNavigationModel): Promise<void> {
    if (model.currentSelectedItem instanceof ProjectGroupVO) {
      this.projectNavigationService.editGroup(model, model.currentSelectedItem);
    } else if( model.currentSelectedItem instanceof ProjectHeadVO) {
      const success = await this.projectComponentPresenter.openEditProjectNameComponent(model.currentSelectedItem, model.compositeCheckBoxValue)
      if (success) {
        this.projectNavigationService.renameProjectSuccess(model, model.currentSelectedItem);
      }
    } else {
      Log.error("Tried to edit an unknown item type:", typeof(model.currentSelectedItem));
    }
  }

  public async pin(model: ProjectNavigationModel, item: ProjectHeadVO | ProjectGroupVO): Promise<void> {
    let toggleSuccess = false;
    if (item instanceof ProjectGroupVO) {
      toggleSuccess = await firstValueFrom(this.projectNavigationService.toggleGroupPinning(model, item));
      Log.assert(toggleSuccess, "Failed to toggle group pinning");
    } else if (item instanceof ProjectHeadVO) {
      toggleSuccess = await firstValueFrom(this.projectNavigationService.toggleProjectPinning(model, item));
      Log.assert(toggleSuccess, "Failed to toggle project pinning");
    } else {
      Log.error("Tried to pin an unknown item type:", typeof(item));
    }
    if (toggleSuccess && model.currentTab === ProjectNavigationTab.Pinned) {
      this._redrawList$.next();
    }
  }

  public async toggleVisibility(model: ProjectNavigationModel): Promise<void> {
    if (model.currentSelectedItem instanceof ProjectGroupVO) {
      const isSuccess = await firstValueFrom(this.projectNavigationService.toggleGroupVisibility(model, model.currentSelectedItem))
      if (isSuccess && !model.searchMode) {
        this._redrawList$.next()
      }
    } else if (model.currentSelectedItem instanceof ProjectHeadVO) {
      // toggling visibility is not implemented for projectHeads
    } else {
      Log.error("Tried to toggle visibility on an unknown item type:", typeof(model.currentSelectedItem));
    }
  }

  public isMoveEnabled(model:ProjectNavigationModel): boolean {
    if (model.currentSelectedItem instanceof ProjectHeadVO) {
      return !!model && !model.currentSelectedItem.isSharedReadOnly
    } else if (model.currentSelectedItem instanceof ProjectGroupVO) {
      return !!model && !(model.currentSelectedItem.isSharedReadOnly) && (
        model.currentSelectedItem.groupType === ProjectGroupUtils.PROJECT ||
        model.currentSelectedItem.groupType === ProjectGroupUtils.FOLDER);
    }
    return false;
  }

  public isDeleteEnabled(model:ProjectNavigationModel): boolean {
    if (model.currentSelectedItem instanceof ProjectHeadVO) {
      return (
        !!model && !model?.compositeCheckBoxValue &&
        this.userModel.user.hasPermission("DELETE_PROJECTS") &&
        model.currentSelectedItem &&
        model.currentSelectedItem.id !== this.projectModel.currentProject.id &&
        UserUtils.hasPermissionToDeleteProject(this.userModel.user, model.currentSelectedItem)
      );
    } else if (model.currentSelectedItem instanceof ProjectGroupVO) {
      return !!model
        && this.projectNavigationService.isDeleteProjectGroupEnabled(model.currentSelectedItem)
        && this.projectModel.currentProject
        && !ProjectGroupUtils.isParentOfCurrentProject(model.currentSelectedItem, this.projectModel.currentProject);
    }
    return false;
  }

  public isCopyEnabled(model:ProjectNavigationModel): boolean {
    if (model.currentSelectedItem instanceof ProjectHeadVO) {
      return !!model && !model.currentSelectedItem.temporaryLinkGUID;
    } else if (model.currentSelectedItem instanceof ProjectGroupVO) {
      return !!model?.currentSelectedItem;
    }
    return false;
  }

  public isChangeOwnerEnabled(model:ProjectNavigationModel): boolean {
    if (model.currentSelectedItem instanceof ProjectHeadVO) {
      return !!model && !model.currentSelectedItem.isSharedReadOnly && (
        this.userModel.user.hasPermission("CHANGE_PROJECT_OWNER_FOR_OWN_PROJECTS") ||
        this.userModel.user.hasPermission("CHANGE_PROJECT_OWNER_FOR_ALL_PROJECTS")
      )
    }
    return false;
  }

  public isToggleVisibilityEnabled(model:ProjectNavigationModel): boolean {
    if (model.currentSelectedItem instanceof ProjectGroupVO) {
      return !!model && model.currentModel?.visibleHiddenProjects === true &&
        model.currentProjectFolder?.groupName !== ProjectNavigationModel.ROOT_NAME_MY_ASSIGNED &&
        ProjectGroupUtils.isConfigurationGroup(model.currentSelectedItem) &&
        model.currentSelectedItem.groupOwner.userId === this.userModel.user.id;
    }
    return false;
  }

  public isEditEnabled(model:ProjectNavigationModel): boolean {
    if (model.currentSelectedItem instanceof ProjectHeadVO) {
      return !!model && UserUtils.hasPermissionToRenameProject(this.userModel.user, model.currentSelectedItem);
    } else if (model.currentSelectedItem instanceof ProjectGroupVO) {
      return !!model && this.projectNavigationService.isEditProjectGroupEnabled(model.currentSelectedItem);
    }
    return false;
  }

  public isPinEnabled(model: ProjectNavigationModel, item: ProjectHeadVO | ProjectGroupVO): boolean {
    if (item instanceof ProjectHeadVO) {
      return !!model && model.visiblePinnedProjects && !model?.compositeCheckBoxValue;
    } else if (item instanceof ProjectGroupVO) {
      return !!model && model.visiblePinnedProjects && !model.compositeCheckBoxValue &&
        ProjectGroupUtils.isConfigurationGroup(item);
    }
    return false;
  }
}
